import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {OfferModeSwitcher} from 'components/OfferModeSwitcher/OfferModeSwitcher'
import {
  getAllTaxesIncluded,
  getCurrencyCode,
  getLanguageCode
} from 'modules/meta/selectors'
import {
  getShowTotalPrices,
  getShowTotalPriceToggle
} from 'modules/sapiSearch/selectors'
import {setShowTotalPrices} from 'modules/search/slice'
import {BOOKING_MANAGEMENT_PATH} from 'utils/bookingManagement/urls'

import {Variant} from '@vio/atlas/src/components/designSystem/Button/Button'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'
import {PreferencesWidget} from '@vio/shared/src/user/preferences/PreferencesWidget'

interface Props {
  /** Preferences button variant */
  preferencesButtonVariant?: Variant
}

export const PreferencesWidgetWithStore = ({
  preferencesButtonVariant
}: Props) => {
  const dispatch = useDispatch()
  const {navigateWithParameters} = useUrlUpdater()
  const currentCurrency = useSelector(getCurrencyCode)
  const currentLanguage = useSelector(getLanguageCode)
  const {pathname} = useRouterProvider()
  const showTotalPrices = useSelector(getShowTotalPrices)
  const showTotalPriceToggle = useSelector(getShowTotalPriceToggle)
  const includeTaxes = useSelector(getAllTaxesIncluded)

  const enabledCurrencies = getEnabledCurrencies(
    process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
  )

  const isBookingManagementPage = pathname.startsWith(BOOKING_MANAGEMENT_PATH)

  const handlePreferencesChange = useCallback(
    async ({hasCurrencyChanged, hasLanguageChanged, currency, language}) => {
      if (hasLanguageChanged) {
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.LanguageChange,
            action: Action.Submitted
          })
        )
      }

      if (hasCurrencyChanged) {
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.CurrencyChange,
            action: Action.Submitted
          })
        )
      }

      const buildLanguageParams = () => {
        if (hasLanguageChanged) {
          return {
            lang: language,
            forceLanguageChange: 1 as BooleanUrlParam
          }
        }
      }

      const buildCurrencyParams = () => {
        if (hasCurrencyChanged) {
          return {
            currency,
            forceCurrencyChange: 1 as BooleanUrlParam,
            priceMin: undefined,
            priceMax: undefined,
            clickedOfferRate: undefined
          }
        }
      }

      setTimeout(() => {
        navigateWithParameters(
          {
            ...buildLanguageParams(),
            ...buildCurrencyParams()
          },
          {
            basePath: pathname
          }
        )
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
    },
    [dispatch, navigateWithParameters, pathname]
  )

  const handlePreferencesLinkClick = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.LanguageCurrencyPreferencesButton,
        action: Action.Clicked
      })
    )
  }, [dispatch])

  const onChangePriceDisplay = useCallback(
    (showTotal: boolean) => {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceToggle,
          action: Action.Clicked
        })
      )
      dispatch(setShowTotalPrices(showTotal))
    },
    [dispatch]
  )

  return (
    <>
      <OfferModeSwitcher variant={preferencesButtonVariant} />
      <PreferencesWidget
        currentCurrency={currentCurrency}
        currentLanguage={currentLanguage}
        enabledCurrencies={enabledCurrencies}
        hasCurrency={!isBookingManagementPage}
        showPriceDisplaySetting={
          !isBookingManagementPage && showTotalPriceToggle
        }
        showTotalPrices={showTotalPrices}
        onChangePriceDisplay={onChangePriceDisplay}
        onPreferencesChange={handlePreferencesChange}
        onPreferencesLinkClick={handlePreferencesLinkClick}
        includeTaxes={includeTaxes}
        preferencesButtonVariant={preferencesButtonVariant}
      />
    </>
  )
}
